import type { ScreenRect } from "../../battlefield-3d/hand-screen-layout";

export interface MatchHudLayout {
  readonly viewportWidth: number;
  readonly viewportHeight: number;
  readonly topConsole: ScreenRect;
  readonly statusMessage: ScreenRect;
  readonly menu: ScreenRect;
  readonly pauseAi: ScreenRect;
  readonly fastAi: ScreenRect;
  readonly phaseAction: ScreenRect;
}

function rect(left: number, top: number, width: number, height: number): ScreenRect {
  return { left, top, width, height };
}

export function createMatchHudLayout(
  width: number,
  height: number,
  phaseActionSide: "left" | "right" = "right",
): MatchHudLayout {
  const safeWidth = Math.max(1, width);
  const safeHeight = Math.max(1, height);
  const landscape = safeHeight <= safeWidth;
  const edge = landscape ? 10 : Math.max(10, Math.min(14, safeWidth * 0.03));
  const topHeight = landscape
    ? Math.max(60, Math.min(68, safeHeight * 0.19))
    : Math.max(88, Math.min(112, safeHeight * 0.13));
  const topWidth = Math.min(safeWidth - edge * 2, landscape ? 720 : 520);
  const topLeft = (safeWidth - topWidth) / 2;
  const menuSize = landscape ? 48 : 52;
  const statusTop = edge + topHeight + 7;
  const statusHeight = landscape ? 28 : 34;
  const statusWidth = Math.min(topWidth - (landscape ? 120 : 38), 440);
  const phaseWidth = 112;
  const phaseHeight = landscape ? 48 : 54;
  const phaseTop = Math.max(
    statusTop + statusHeight + 12,
    landscape ? safeHeight * 0.47 : safeHeight * 0.73,
  );
  const phaseEdge = landscape ? edge : Math.max(5, edge / 2);
  const phaseLeft = phaseActionSide === "left" ? phaseEdge : safeWidth - phaseWidth - phaseEdge;

  return {
    viewportWidth: safeWidth,
    viewportHeight: safeHeight,
    topConsole: rect(topLeft, edge, topWidth, topHeight),
    statusMessage: rect((safeWidth - statusWidth) / 2, statusTop, statusWidth, statusHeight),
    menu: rect(topLeft + topWidth - menuSize - 5, edge + 5, menuSize, menuSize),
    pauseAi: rect(topLeft + topWidth - menuSize * 3 - 17, edge + 5, menuSize, menuSize),
    fastAi: rect(topLeft + topWidth - menuSize * 2 - 11, edge + 5, menuSize, menuSize),
    phaseAction: rect(phaseLeft, phaseTop, phaseWidth, phaseHeight),
  };
}

export function rectInsideViewport(rectangle: ScreenRect, layout: MatchHudLayout): boolean {
  return (
    rectangle.left >= 0 &&
    rectangle.top >= 0 &&
    rectangle.left + rectangle.width <= layout.viewportWidth &&
    rectangle.top + rectangle.height <= layout.viewportHeight
  );
}

export function screenRectCentre(rectangle: ScreenRect): readonly [number, number] {
  return [rectangle.left + rectangle.width / 2, rectangle.top + rectangle.height / 2];
}

export function screenRectsOverlap(first: ScreenRect, second: ScreenRect): boolean {
  return !(
    first.left + first.width <= second.left ||
    second.left + second.width <= first.left ||
    first.top + first.height <= second.top ||
    second.top + second.height <= first.top
  );
}
